import React, { useMemo, useState, useEffect, useRef, useCallback } from "react";
import { Modal, InputNumber, Typography, Tooltip } from "antd";
import {
  ChevronLeft,
  ChevronRight,
  Calendar,
  Box,
  Layers,
  Edit3,
  DollarSign,
  AlertCircle,
  Package,
} from "react-feather";
import "./ariGrid.css";

const { Text } = Typography;

const currencySymbol = (code) => {
  if (code === "INR") return "₹";
  if (code === "GBP") return "£";
  if (code === "USD") return "$";
  return "";
};

const cellKeyAvailability = (pmsRoomId, date) => `A|${pmsRoomId}|${date}`;
const cellKeyRate = (ratePlanId, occ, date) => `R|${ratePlanId}|${occ}|${date}`;

const fmtHeaderDate = (iso) =>
  new Date(iso).toLocaleDateString(undefined, {
    weekday: "short",
    day: "2-digit",
    month: "short",
    year: "numeric",
  });

const isWeekend = (iso) => {
  const d = new Date(iso).getDay();
  return d === 0 || d === 6;
};

const isToday = (iso) => {
  const d = new Date(iso);
  const t = new Date();
  return d.getFullYear() === t.getFullYear() && d.getMonth() === t.getMonth() && d.getDate() === t.getDate();
};

function getAvailabilityTone(val) {
  const n = Number(val || 0);
  if (n <= 0) return "zero";
  if (n <= 2) return "low";
  return "normal";
}

function getRateTone(val) {
  const n = Number(val || 0);
  if (n <= 0) return "zero";
  return "normal";
}

export default function AriGrid({
  grid,
  draft,
  setDraft,
  viewMode = "rate_availability",
  roomFilter = null,
  rateFilter = null,
  startDateLabel,
  onPrev,
  onNext,
  onPickStartDate,
  onEditRatePlan,
}) {
  const dates = grid?.dates || [];
  const roomsRaw = grid?.rooms || [];

  const showAvailability = viewMode !== "only_rate";
  const showRates = viewMode !== "only_availability";

  const rooms = useMemo(() => {
    let out = roomsRaw;

    if (roomFilter) {
      out = out.filter((r) => String(r.pms_room_type_id) === String(roomFilter));
    }

    if (rateFilter) {
      out = out.map((r) => ({
        ...r,
        ratePlans: (r.ratePlans || []).filter((rp) => String(rp.id) === String(rateFilter)),
      }));
    }

    return out;
  }, [roomsRaw, roomFilter, rateFilter]);

  const [edit, setEdit] = useState(null);
  const inputRef = useRef(null);

  useEffect(() => {
    if (edit) {
      setTimeout(() => inputRef.current?.focus?.(), 30);
    }
  }, [edit]);

  const applyEdit = (val) => {
    if (!edit) return;
    setDraft((prev) => ({ ...prev, [edit.key]: val }));
    setEdit(null);
  };

  const renderValue = (key, fallback) =>
    Object.prototype.hasOwnProperty.call(draft, key) ? draft[key] : fallback;

  const wrapRef = useRef(null);
  const [scrolledX, setScrolledX] = useState(false);
  const [scrolledY, setScrolledY] = useState(false);

  useEffect(() => {
    const el = wrapRef.current;
    if (!el) return;

    const onScroll = () => {
      setScrolledX(el.scrollLeft > 2);
      setScrolledY(el.scrollTop > 2);
    };

    onScroll();
    el.addEventListener("scroll", onScroll, { passive: true });
    return () => el.removeEventListener("scroll", onScroll);
  }, []);

  const focusCell = useCallback((nextEl) => {
    if (nextEl?.focus) nextEl.focus();
  }, []);

  const onGridKeyDown = (e) => {
    const active = document.activeElement;
    if (!active || !active.dataset?.ariCell) return;
    if (edit) return;

    const row = Number(active.dataset.row);
    const col = Number(active.dataset.col);

    const selector = (r, c) => `[data-ari-cell="1"][data-row="${r}"][data-col="${c}"]`;

    if (e.key === "Enter") {
      active.click();
      e.preventDefault();
      return;
    }
    if (e.key === "ArrowRight") {
      focusCell(wrapRef.current?.querySelector(selector(row, col + 1)));
      e.preventDefault();
    }
    if (e.key === "ArrowLeft") {
      focusCell(wrapRef.current?.querySelector(selector(row, col - 1)));
      e.preventDefault();
    }
    if (e.key === "ArrowDown") {
      focusCell(wrapRef.current?.querySelector(selector(row + 1, col)));
      e.preventDefault();
    }
    if (e.key === "ArrowUp") {
      focusCell(wrapRef.current?.querySelector(selector(row - 1, col)));
      e.preventDefault();
    }
  };

  const gridRows = useMemo(() => {
    const out = [];
    rooms.forEach((room) => {
      if (showAvailability) out.push({ kind: "AVL", room, ratePlan: null });
      if (showRates) {
        (room.ratePlans || []).forEach((rp) => out.push({ kind: "RATE", room, ratePlan: rp }));
      }
    });
    return out;
  }, [rooms, showAvailability, showRates]);

  const header = (
    <div
      className={["ari-row", "ari-row--header", scrolledY ? "ari-shadow-top" : ""].join(" ")}
      style={{ ["--ari-days"]: dates.length }}
    >
      <div
        className={[
          "ari-cell",
          "ari-cell--left",
          "ari-sticky-left-1",
          "ari-header-left",
          scrolledX ? "ari-shadow-left-1" : "",
        ].join(" ")}
      >
        <div className="ari-headerLeftWrap">
          <div className="ari-headerTitleWrap">
            <Layers size={16} />
            <Text className="ari-headerTitle">Room / Rate</Text>
          </div>

          <div className="ari-dateNav">
            <button className="ari-iconBtn" onClick={onPrev} type="button" aria-label="Previous range">
              <ChevronLeft size={16} />
            </button>

            <button className="ari-datePill" onClick={onPickStartDate} type="button" title="Change start date">
              <Calendar size={14} />
              <span>{startDateLabel}</span>
            </button>

            <button className="ari-iconBtn" onClick={onNext} type="button" aria-label="Next range">
              <ChevronRight size={16} />
            </button>
          </div>
        </div>
      </div>

      <div
        className={[
          "ari-cell",
          "ari-cell--left",
          "ari-sticky-left-2",
          "ari-header-left",
          "ari-headerTypeCol",
          scrolledX ? "ari-shadow-left-2" : "",
        ].join(" ")}
      >
        <div className="ari-headerTitleWrap">
          <Package size={16} />
          <Text className="ari-headerTitle">Type</Text>
        </div>
      </div>

      {dates.map((d) => {
        const today = isToday(d);
        const weekend = isWeekend(d);
        return (
          <div
            key={d}
            className={[
              "ari-cell",
              "ari-cell--date",
              "ari-sticky-top",
              today ? "ari-col-today-head" : "",
              weekend ? "ari-col-weekend-head" : "",
            ].join(" ")}
          >
            <div className="ari-dateTop">
              {new Date(d).toLocaleDateString(undefined, { weekday: "short" })}
            </div>
            <div className="ari-dateBot">
              {new Date(d).toLocaleDateString(undefined, { day: "2-digit", month: "short" })}
            </div>
          </div>
        );
      })}
    </div>
  );

  return (
    <div
      ref={wrapRef}
      className="ari-wrap"
      tabIndex={0}
      onKeyDown={onGridKeyDown}
      aria-label="Rates and availability grid"
    >
      <div className="ari-table">
        {header}

        {rooms.map((room) => {
          const roomBand = (
            <div
              key={`band-${room.pms_room_type_id}`}
              className="ari-row ari-roomBand"
              style={{ ["--ari-days"]: dates.length }}
            >
              <div
                className={[
                  "ari-cell",
                  "ari-cell--left",
                  "ari-sticky-left-1",
                  "ari-roomBandLeft",
                  scrolledX ? "ari-shadow-left-1" : "",
                ].join(" ")}
              >
                <div className="ari-roomBandTitleWrap">
                  <Box size={16} />
                  <div className="ari-roomName">{room.name}</div>
                </div>
                <div className="ari-roomMeta">Total rooms: {room.total_rooms}</div>
              </div>

              <div
                className={[
                  "ari-cell",
                  "ari-cell--left",
                  "ari-sticky-left-2",
                  "ari-roomBandType",
                  scrolledX ? "ari-shadow-left-2" : "",
                ].join(" ")}
              >
                —
              </div>

              {dates.map((d) => (
                <div key={`band-fill-${room.pms_room_type_id}-${d}`} className="ari-cell ari-roomBandFill" />
              ))}
            </div>
          );

          const avlRow =
            showAvailability && (
              <div
                key={`avl-${room.pms_room_type_id}`}
                className="ari-row ari-row--data ari-row--avl"
                style={{ ["--ari-days"]: dates.length }}
              >
                <div
                  className={[
                    "ari-cell",
                    "ari-cell--left",
                    "ari-sticky-left-1",
                    scrolledX ? "ari-shadow-left-1" : "",
                  ].join(" ")}
                >
                  <div className="ari-leftTextWrap">
                    <Package size={15} />
                    <span className="ari-leftText">Availability</span>
                  </div>
                </div>

                <div
                  className={[
                    "ari-cell",
                    "ari-cell--left",
                    "ari-sticky-left-2",
                    "ari-typeCell",
                    scrolledX ? "ari-shadow-left-2" : "",
                  ].join(" ")}
                >
                  <span className="ari-pill ari-pill--avl">AVL</span>
                </div>

                {dates.map((d, colIndex) => {
                  const k = cellKeyAvailability(room.pms_room_type_id, d);
                  const val = Number(renderValue(k, room.availability?.[d] ?? 0));
                  const today = isToday(d);
                  const weekend = isWeekend(d);
                  const tone = getAvailabilityTone(val);

                  const rowIndex = gridRows.findIndex(
                    (x) => x.kind === "AVL" && x.room.pms_room_type_id === room.pms_room_type_id
                  );

                  return (
                    <button
                      key={k}
                      data-ari-cell="1"
                      data-row={rowIndex}
                      data-col={colIndex}
                      data-index={`${rowIndex}-${colIndex}`}
                      className={[
                        "ari-cell",
                        "ari-cell--val",
                        "ari-cell--avl",
                        draft[k] !== undefined ? "ari-dirty" : "",
                        today ? "ari-col-today" : "",
                        weekend ? "ari-col-weekend" : "",
                        tone === "low" ? "ari-cell-low" : "",
                        tone === "zero" ? "ari-cell-zero-strong" : "",
                      ].join(" ")}
                      onClick={() =>
                        setEdit({
                          key: k,
                          value: val,
                          dateIso: d,
                          kind: "Availability",
                          currency: null,
                          label: `${room.name} • Availability`,
                        })
                      }
                      type="button"
                    >
                      <span className="ari-val">{val}</span>
                    </button>
                  );
                })}
              </div>
            );

          const rateRows =
            showRates &&
            (room.ratePlans || []).map((rp) => {
              const occ = Number(rp.occ ?? rp.occupancy ?? 1);

              const rowIndex = gridRows.findIndex(
                (x) =>
                  x.kind === "RATE" &&
                  x.room.pms_room_type_id === room.pms_room_type_id &&
                  x.ratePlan?.id === rp.id
              );

              return (
                <div
                  key={`rate-${room.pms_room_type_id}-${rp.id}`}
                  className="ari-row ari-row--data ari-row--rate"
                  style={{ ["--ari-days"]: dates.length }}
                >
                  <div
                    className={[
                      "ari-cell",
                      "ari-cell--left",
                      "ari-sticky-left-1",
                      scrolledX ? "ari-shadow-left-1" : "",
                    ].join(" ")}
                  >
                    <div className="ari-rateRowHeader">
                      <div className="ari-leftTextWrap" style={{ minWidth: 0 }}>
                        <DollarSign size={15} />
                        <span className="ari-leftText ari-rateTitle">{rp.title}</span>
                      </div>

                      <Tooltip title="Edit rate plan">
                        <button
                          type="button"
                          onClick={(e) => {
                            e.stopPropagation();
                            onEditRatePlan?.({
                              ...rp,
                              pms_room_type_id: room.pms_room_type_id,
                              room_name: room.name,
                              currency: grid.currency,
                            });
                          }}
                          className="ari-editPlanBtn"
                        >
                          <Edit3 size={13} />
                          Edit Plan
                        </button>
                      </Tooltip>
                    </div>
                  </div>

                  <div
                    className={[
                      "ari-cell",
                      "ari-cell--left",
                      "ari-sticky-left-2",
                      "ari-typeCell",
                      scrolledX ? "ari-shadow-left-2" : "",
                    ].join(" ")}
                  >
                    <span className="ari-pill ari-pill--rate">RATE</span>
                    <span className="ari-occ">×{occ}</span>
                  </div>

                  {dates.map((d, colIndex) => {
                    const k = cellKeyRate(rp.id, occ, d);

                    const fallbackRate =
                      rp.rates?.[d] && typeof rp.rates[d] === "object"
                        ? rp.rates[d]?.[occ] ?? 0
                        : rp.rates?.[d] ?? 0;

                    const val = Number(renderValue(k, fallbackRate));
                    const today = isToday(d);
                    const weekend = isWeekend(d);
                    const tone = getRateTone(val);

                    return (
                      <button
                        key={k}
                        data-ari-cell="1"
                        data-row={rowIndex}
                        data-col={colIndex}
                        data-index={`${rowIndex}-${colIndex}`}
                        className={[
                          "ari-cell",
                          "ari-cell--val",
                          "ari-cell--rate",
                          draft[k] !== undefined ? "ari-dirty" : "",
                          today ? "ari-col-today" : "",
                          weekend ? "ari-col-weekend" : "",
                          tone === "zero" ? "ari-cell-zero-strong" : "",
                        ].join(" ")}
                        onClick={() =>
                          setEdit({
                            key: k,
                            value: val,
                            dateIso: d,
                            kind: `Rate • ×${occ}`,
                            currency: grid.currency,
                            label: `${room.name} • ${rp.title}`,
                          })
                        }
                        type="button"
                      >
                        <span className="ari-val">
                          <span className="ari-currency">{currencySymbol(grid.currency)}</span>
                          {val.toLocaleString()}
                        </span>
                      </button>
                    );
                  })}
                </div>
              );
            });

          return (
            <React.Fragment key={room.pms_room_type_id}>
              {roomBand}
              {avlRow}
              {rateRows}
            </React.Fragment>
          );
        })}
      </div>

      <Modal
        open={!!edit}
        title="Update value"
        onCancel={() => setEdit(null)}
        onOk={() => applyEdit(edit?.value)}
        okText="Apply"
      >
        <div style={{ marginBottom: 10 }}>
          <div style={{ fontWeight: 800, color: "rgba(15,23,42,.92)" }}>{edit?.label}</div>
          <div style={{ color: "rgba(15,23,42,.55)", marginTop: 2 }}>
            {edit?.kind} • {edit?.dateIso ? fmtHeaderDate(edit.dateIso) : ""}
          </div>
        </div>

        <InputNumber
          ref={inputRef}
          style={{ width: "100%" }}
          min={0}
          value={edit?.value}
          onChange={(v) => setEdit((p) => ({ ...p, value: v }))}
          addonBefore={edit?.currency ? currencySymbol(edit.currency) : null}
        />

        <div className="ari-editHint">
          <AlertCircle size={14} />
          <span>
            Tip: Use <b>arrow keys</b> to move. Press <b>Enter</b> to edit.
          </span>
        </div>
      </Modal>
    </div>
  );
}